import type { EstabrookContext } from "service/api/estabrookContext";
import config from "../config";

/**
 * Plow-report downloads.
 *
 * The backend returns a real file (currently .xlsx) with its name in the
 * `Content-Disposition` header, but the generated fetcher (`estabrookFetch`)
 * only ever returns `response.blob()` — it discards response headers. These
 * fetch the endpoints directly (mirroring `estabrookFetch`'s auth/refresh
 * handling) so the filename shown to the user always matches what the backend
 * actually sent, instead of a name/extension guessed on the frontend.
 *
 * `fetcherOptions` comes from `useEstabrookContext().fetcherOptions` at the
 * call site so auth + token refresh behave like every other request.
 */
type FetcherOptions = EstabrookContext["fetcherOptions"];

export interface PlowReportFile {
  blob: Blob;
  filename: string;
}

const FALLBACK_FILENAME = "plow-report.xlsx";

const filenameFromContentDisposition = (
  contentDisposition: string | null,
): string =>
  contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1] ?? FALLBACK_FILENAME;

/**
 * Isolates the one bit of `estabrookFetch` we can't reuse directly (it lives
 * in a generated file and can't be extended to expose response headers), so
 * that if the auth/refresh flow changes there, this is the one place to
 * mirror it.
 */
const buildAuthHeader = async (
  fetcherOptions: FetcherOptions,
): Promise<{ authorization?: string }> => {
  const tokens = await fetcherOptions.refreshToken?.();
  return tokens ? { authorization: `Bearer ${tokens.accessToken}` } : {};
};

const postForFile = async (
  fetcherOptions: FetcherOptions,
  path: string,
  body: object,
): Promise<PlowReportFile> => {
  const response = await window.fetch(`${config.baseURL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...fetcherOptions.headers,
      ...(await buildAuthHeader(fetcherOptions)),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw await response.json().catch(() => ({
      statusCode: "unknown" as const,
      errors: [{ code: "unknown", detail: "Failed to download plow report." }],
    }));
  }

  return {
    blob: await response.blob(),
    filename: filenameFromContentDisposition(
      response.headers.get("content-disposition"),
    ),
  };
};

// POST /api/v2/admin/routes/plow-report  (body: { date })
export const fetchShiftPlowReport = (
  fetcherOptions: FetcherOptions,
  date: string,
): Promise<PlowReportFile> =>
  postForFile(fetcherOptions, "/api/v2/admin/routes/plow-report", { date });

export interface CustomerPlowReportParams {
  /** Location ids to include in the report. */
  locations: string[];
  /** Start date, YYYY-MM-DD. */
  from: string;
  /** End date, YYYY-MM-DD. */
  to: string;
}

// POST /api/v2/admin/locations/plow-report  (body: { locations, from, to })
export const fetchCustomerPlowReport = (
  fetcherOptions: FetcherOptions,
  params: CustomerPlowReportParams,
): Promise<PlowReportFile> =>
  postForFile(fetcherOptions, "/api/v2/admin/locations/plow-report", params);
